export const normalizeCapabilityPrompt = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isFreshLegalDocumentTitleSearchPrompt = (value = '') => {
    const normalized = normalizeCapabilityPrompt(value);
    if (!/\b(?:fetch|find|search|locate|pull|list|any|all|related to|for|regarding)\b/.test(normalized)) return false;
    if (/\b(?:selected|attached|uploaded|this file|this document|this source|that file|that document|that source)\b/.test(normalized)) return false;
    return /\b(?:demand letters?|settlement offers?|distribution memos?|demand packages?|petitions?|motions?|orders?|discovery responses?|discovery requests?)\b/.test(normalized);
};

export const filterReferencesForFreshTitleSearch = (references = [], query = '') => {
    const referenceList = Array.isArray(references) ? references : [];
    if (!isFreshLegalDocumentTitleSearchPrompt(query)) return referenceList;
    return referenceList.filter(reference => !Number(reference?.file_id));
};

export const isLikelyWorkspaceNavigationPrompt = (value = '') => {
    const normalized = normalizeCapabilityPrompt(value);
    if (!normalized) return false;
    // Mailbox reads and Outlook summary automation belong to Jane's
    // mailbox-isolated runtime. This check must precede the intentionally
    // broad "show me" + "case/matter" workspace heuristics below.
    if (isOutlookMailPrompt(normalized)) return false;
    if (/\b(?:activity\s+feed|feed\s+(?:entry|note|draft)|draft\s+(?:a\s+)?(?:feed\s+entry|note|task|activity)|start\s+(?:a\s+)?draft|create\s+(?:a\s+)?draft)\b/.test(normalized)) {
        return true;
    }
    if (/\b(?:draft|create|start|prepare|open)\b.{0,80}\b(?:expense|cost|payee|reimbursement|billing\s+expense|billing\s+entry|billing\s+entries)\b|\b(?:expense|cost|billing)\s+(?:entry|draft|entries)\b|\bdraft\b.{0,80}\b(?:amount|\$\s*[0-9])/.test(normalized)) {
        return true;
    }
    if (/\b(?:do\s+not\s+post|dont\s+post|not\s+post|leave\s+as\s+draft)\b/.test(normalized)) {
        return true;
    }
    if (/\b(?:where|take\s+me|show\s+me|open|go\s+to|locate|find)\b/.test(normalized) && /\b(?:document|file|invoice|record|letter|petition|motion|order|medical|billing|exists|lives|located|location|folder|section)\b/.test(normalized)) {
        return true;
    }
    if (/\b(?:take\s+me|show\s+me|open|go\s+to|navigate|bring\s+up)\b/.test(normalized) && /\b(?:expenses?|costs?|case\s+expenses?)\b/.test(normalized)) {
        return true;
    }
    if (/\b(?:take\s+me\s+to|show\s+me|open|go\s+to|navigate\s+to|bring\s+up)\b/.test(normalized) && /\b(?:case|matter)\b/.test(normalized)) {
        return true;
    }
    return false;
};

export const isLikelyCaseBoundFollowupPrompt = (value = '') => {
    const normalized = normalizeCapabilityPrompt(value);
    if (!normalized) return false;
    return /\b(?:this|that|the|same|current|selected|referenced|attached|uploaded)\s+(?:case|matter|document|file|source|scheduling\s+order)\b/.test(normalized)
        || /\b(?:case|matter)\s+(?:we\s+)?(?:were\s+)?(?:just\s+)?(?:discussing|discussed|mentioned|referenced|selected)\b/.test(normalized)
        || /\b(?:what'?s\s+missing|next\s+steps?|timeline|chronology|calendar\s+drafts?|calendar\s+entries?|deadline\s+drafts?|show\s+.*(?:orders?|motions?|documents?|files?))\b/.test(normalized);
};

export const extractWorkspaceCaseCandidate = (value = '') => {
    const prompt = String(value || '').replace(/\s+/g, ' ').trim();
    if (!prompt) return '';

    const normalizeCandidate = (candidateValue = '') => {
        let candidate = String(candidateValue || '').trim().replace(/^[\s.:;!?—–-]+|[\s.:;!?—–-]+$/g, '');
        candidate = candidate.replace(/^(?:the|this|that)\s+/i, '');
        candidate = candidate.replace(/^(?:activity\s+feed|feed\s+entry|feed|draft|note|task)\s+(?:in|for|of)\s+/i, '');
        candidate = candidate.replace(/^(?:(?:expenses?|costs?|case\s+expenses?|documents?|files?|medical|billing)\s+)?section\s+(?:in|for|of|on|to)\s+/i, '');
        candidate = candidate.replace(/^(?:expenses?|costs?|case\s+expenses?)\s+(?:section\s+)?(?:in|for|of|on|to)\s+/i, '');
        candidate = candidate.replace(/^.+?\s+section\s+(?:in|for|of|on|to)\s+/i, '');
        candidate = candidate.replace(/^(?:the|this|that)\s+/i, '');
        candidate = candidate.replace(/\b(?:expenses?|costs?|case\s+expenses?|documents?|files?|medical|billing)\s+section\b/i, '');
        candidate = candidate.replace(/\b(?:case|matter|file|please)\b.*$/i, '').trim().replace(/^[\s.:;!?—–-]+|[\s.:;!?—–-]+$/g, '');
        return /^(?:current|active|opened|selected|this|that)$/i.test(candidate) ? '' : candidate;
    };

    const isGenericCandidate = (candidateValue = '') => {
        const normalized = String(candidateValue || '').trim().toLowerCase();
        if (!normalized || normalized.length < 3) return true;
        if (/^(?:open|show|take|go|navigate|bring)\b/i.test(normalized)) return true;
        if (/\b(?:calendar|draft|drafts)\b/i.test(normalized)) return true;
        return [
            'the', 'this', 'that', 'case', 'matter', 'calendar', 'draft', 'drafts',
            'current', 'selected', 'active', 'opened', 'open', 'review', 'these', 'those'
        ].includes(normalized);
    };

    const priorityPatterns = [
        /\b(?:take\s+me\s+to|show\s+me|open|go\s+to|navigate\s+to|bring\s+up)\s+(?:the\s+)?(?:expenses?|costs?|case\s+expenses?)\s+section\s+(?:in|for|of|on|to)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
        /\b(?:expenses?|costs?|case\s+expenses?)\s+section\s+(?:in|for|of|on|to)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
        /\b(?:take\s+me\s+to|show\s+me|open|go\s+to|navigate\s+to|bring\s+up)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
        /\bactivity\s+feed\s+(?:in|for|of)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
        /\b(?:draft|create|start)\b.*?\b(?:in|for|of)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/i,
    ];
    for (const pattern of priorityPatterns) {
        const match = prompt.match(pattern);
        const candidate = normalizeCandidate(match?.[1] || '');
        if (candidate && !isGenericCandidate(candidate)) return candidate;
    }

    const genericMatches = [...prompt.matchAll(/\b(?:in|for|of|on|to|regarding|about|re:)\s+(?:the\s+)?(.+?)\s+(?:case|matter)\b/gi)];
    for (let index = genericMatches.length - 1; index >= 0; index -= 1) {
        const candidate = normalizeCandidate(genericMatches[index]?.[1] || '');
        if (candidate && !isGenericCandidate(candidate)) return candidate;
    }

    const trailingMatch = prompt.match(/\b([A-Z][\p{L}\p{M}'’.-]+(?:\s+[A-Za-z][\p{L}\p{M}'’.-]+){1,5})\s+(?:case|matter)\b/u)
        || prompt.match(/\b([a-z][\p{L}\p{M}'’.-]+(?:\s+[a-z][\p{L}\p{M}'’.-]+){1,5})\s+(?:case|matter)\b/iu);
    const trailingCandidate = normalizeCandidate(trailingMatch?.[1] || '');
    return trailingCandidate && !isGenericCandidate(trailingCandidate) ? trailingCandidate : '';
};

export const isLegalDraftPrompt = (value = '') => {
    const query = String(value || '').trim();
    if (!query || !/\b(?:draft|compose|write|generate|create|prepare|make)\b/i.test(query)) {
        return false;
    }

    return /\b(?:demands?|demand\s+letters?|policy\s+limits?\s+demand|stowers\s+demand|discovery\s+responses?|respond\s+to\s+discovery|discovery\s+requests?|interrogator(?:y|ies)|requests?\s+for\s+production|requests?\s+for\s+admissions?|(?:rfp|rfa)s?|mediation\s+(?:statement|letter|brief)|settlement\s+(?:offer|proposal|letter)|offer\s+to\s+settle|letters?)\b/i.test(query);
};

export const isOutlookMailPrompt = (value = '') => {
    const normalized = normalizeCapabilityPrompt(value);
    if (!normalized) return false;
    // An explicit CaseDB Activity Feed draft remains a workspace operation,
    // even when its subject happens to mention an Outlook summary.
    if (/\b(?:activity\s+feed|feed\s+(?:entry|note|draft))\b/.test(normalized)) return false;
    if (/\b(?:selected|casedb|case) (?:documents?|files?)\b/.test(normalized)
        && !/\b(?:outlook|mailbox|inbox|sent (?:mail|items?))\b/.test(normalized)) {
        return false;
    }
    const hasMailboxAnchor = /\b(?:outlook|mailbox|inbox|sent (?:mail|items?)|my (?:work )?emails?|emails? (?:from|to|between|with|received|sent|about|regarding)|(?:email|mail) (?:thread|conversation|digest|summary))\b/.test(normalized);
    if (!hasMailboxAnchor) return false;
    const hasOutlookProviderAnchor = /\b(?:outlook|mailbox|inbox|my (?:work )?emails?)\b/.test(normalized);
    const hasSummaryAnchor = /\b(?:summary|summari[sz]e|digest|recap|brief)\b/.test(normalized);
    const hasSummaryAutomationIntent = hasOutlookProviderAnchor && hasSummaryAnchor
        && /\b(?:schedule|proposed schedule|automate|configure|set up|set (?:one|a|the) daily|enable|disable|pause|resume|stop|cancel|turn (?:on|off)|change|update|every (?:day|morning|evening)|each day)\b/.test(normalized);
    if (hasSummaryAutomationIntent) return true;
    const hasReadIntent = /\b(?:find|search|look up|show|list|read|review|summari[sz]e|summary|brief|recap|digest|catch me up|what|which|when|who|where|did|does|has|have|received|sent|between)\b/.test(normalized);
    const hasWriteOnlyIntent = /\b(?:draft|write|compose|reply|send|forward|file|save|move|delete|schedule|enable|configure)\b/.test(normalized)
        && !hasReadIntent;
    if (/\b(?:schedule|enable|configure)\b/.test(normalized)) return false;
    return hasReadIntent && !hasWriteOnlyIntent;
};

// Backward-compatible name used by focused routing tests and any older UI
// imports. Keep one classifier so Auto, Long, reruns, and v2 gating cannot
// drift apart.
export const isJaneOutlookMailReadPrompt = isOutlookMailPrompt;

export const shouldUseDirectWorkspaceContinuation = ({
    pendingActionType = '',
    query = '',
} = {}) => String(pendingActionType || '') === 'workspace_case_selection'
    && /\b(?:case\s*(?:id|number|no\.?|#)?\s*[:#-]?\s*)?\d{3,12}\b/i.test(String(query || '').trim());

const joinPromptContextParts = (parts = []) => {
    if (parts.length <= 1) return parts[0] || '';
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};

const getPromptReferenceFallbackIdentity = (reference = {}, index = 0) => String(
    reference.url
    || reference.file_path
    || reference.path
    || reference.label
    || reference.file_name
    || reference.name
    || reference.id
    || index
).trim();

export const buildJanePromptContextSummary = (references = [], selectedSourcesOnly = false) => {
    const referenceList = Array.isArray(references) ? references.filter(Boolean) : [];
    const fileReferences = new Set();
    const caseContexts = new Set();
    const otherSources = new Set();

    referenceList.forEach((reference, index) => {
        const type = String(reference?.type || reference?.source_type || '').trim().toLowerCase();
        const isCaseContext = ['case', 'case_profile', 'case_summary'].includes(type);
        const isFileReference = Number(reference?.file_id || reference?.fileId || 0) > 0
            || /(?:file|document|attachment|upload)/.test(type);
        const fallbackIdentity = getPromptReferenceFallbackIdentity(reference, index);

        if (isCaseContext) {
            caseContexts.add(`case-${Number(reference?.case_id || reference?.caseId || reference?.id || 0) || fallbackIdentity}`);
        } else if (isFileReference) {
            fileReferences.add(`file-${Number(reference?.file_id || reference?.fileId || reference?.id || 0) || fallbackIdentity}`);
        } else {
            otherSources.add(`source-${fallbackIdentity}`);
        }
    });

    const fileCount = fileReferences.size;
    const caseContextCount = caseContexts.size;
    const otherSourceCount = otherSources.size;
    const descriptionParts = [];
    const labels = [];

    if (fileCount > 0) {
        descriptionParts.push(`${fileCount} selected file${fileCount === 1 ? '' : 's'}`);
        labels.push(`${fileCount} selected file${fileCount === 1 ? '' : 's'}`);
    }
    if (caseContextCount > 0) {
        descriptionParts.push(caseContextCount === 1 ? 'the case context' : `${caseContextCount} case contexts`);
        labels.push(caseContextCount === 1 ? 'Case context' : `${caseContextCount} case contexts`);
    }
    if (otherSourceCount > 0) {
        descriptionParts.push(`${otherSourceCount} selected source${otherSourceCount === 1 ? '' : 's'}`);
        labels.push(`${otherSourceCount} selected source${otherSourceCount === 1 ? '' : 's'}`);
    }

    return {
        fileCount,
        caseContextCount,
        otherSourceCount,
        labels,
        description: joinPromptContextParts(descriptionParts)
            || (selectedSourcesOnly ? 'the selected context' : 'the available CaseDB context'),
    };
};

export const getJaneLegacyIntelligenceEndpoint = ({
    query = '',
    runMode = 'default',
    rerunMessageId = 0,
    isReplacementContinuation = false,
} = {}) => {
    if (isLegalDraftPrompt(query)) return 'library-intelligence-search.php';
    if (isOutlookMailPrompt(query)) return 'jane-loop.php';
    if (Number(rerunMessageId || 0) > 0 || isReplacementContinuation || String(runMode || '').toLowerCase() === 'long') {
        return 'jane-loop.php';
    }
    return 'library-intelligence-search.php';
};

export const shouldUseJaneAgentV2ForPrompt = ({
    query = '',
    runMode = 'default',
    rerunMessageId = 0,
    isReplacementContinuation = false,
    agentV2Enabled = false,
} = {}) => Boolean(agentV2Enabled)
    && String(runMode || '').toLowerCase() !== 'fast'
    && !isLegalDraftPrompt(query)
    // Outlook uses the deterministic PHP controller so a mailbox request
    // cannot be misrouted to case.search while the v2 planner is evolving.
    && !isOutlookMailPrompt(query)
    && Number(rerunMessageId || 0) <= 0
    && !isReplacementContinuation;

export const normalizeJaneLegacyFallbackEndpoint = (value = '') => {
    const endpoint = String(value || '').trim().replace(/^\/+/, '');
    return ['library-intelligence-search.php', 'jane-loop.php'].includes(endpoint)
        ? endpoint
        : 'jane-loop.php';
};

export const isJaneLegalDraftPresentation = ({ answer = null, intelligence = null, metadata = null } = {}) => {
    const normalizedAnswer = answer && typeof answer === 'object' ? answer : {};
    const normalizedMetadata = metadata && typeof metadata === 'object' ? metadata : {};
    const normalizedIntelligence = intelligence && typeof intelligence === 'object'
        ? intelligence
        : (normalizedMetadata.intelligence && typeof normalizedMetadata.intelligence === 'object'
            ? normalizedMetadata.intelligence
            : {});
    const modeValues = [
        normalizedAnswer.mode,
        normalizedIntelligence.retrieval_mode,
        normalizedIntelligence.mode,
    ].map(value => String(value || '').trim().toLowerCase());
    const plan = normalizedIntelligence.plan && typeof normalizedIntelligence.plan === 'object'
        ? normalizedIntelligence.plan
        : {};
    const action = normalizedAnswer.confirmable_action
        || normalizedMetadata.confirmable_action
        || null;
    const proactive = normalizedAnswer.proactive_ai
        || normalizedIntelligence.proactive_ai
        || normalizedMetadata.proactive_ai
        || null;

    return modeValues.some(mode => mode === 'legal_document_draft' || mode.startsWith('background_draft_'))
        || String(plan.route_goal || '').trim().toLowerCase() === 'legal_document_draft'
        || String(plan.runtime_route || '').trim().toLowerCase() === 'legal_draft'
        || Boolean(plan.draft_intent)
        || String(action?.type || '').trim().toLowerCase() === 'save_legal_draft_docx'
        || (
            String(proactive?.task_type || '').trim().toLowerCase() === 'jane_background_run'
            && String(proactive?.route || plan.runtime_route || '').trim().toLowerCase() === 'legal_draft'
        );
};

export const buildRagSearchStatusMessage = (query = '', references = []) => {
    const normalized = normalizeCapabilityPrompt(query);
    const referenceList = Array.isArray(references) ? references : [];
    const hasCaseReference = referenceList.some(reference => ['case', 'case_profile'].includes(String(reference?.type || reference?.source_type || '').toLowerCase()) || reference?.case_id);
    const hasUploadReference = referenceList.some(reference => ['chat_upload', 'upload'].includes(String(reference?.type || reference?.source_type || '').toLowerCase()));
    const hasSpreadsheetReference = referenceList.some(reference => /\.(xlsx?|csv)$/i.test(String(reference?.file_name || reference?.name || reference?.label || '')));

    if (/\b(draft|write|prepare|compose|generate)\b/.test(normalized)) {
        if (/\b(demand|letter)\b/.test(normalized)) return 'Gathering case facts for the demand letter…';
        return 'Gathering the source material for drafting…';
    }
    if (/\b(?:what\s+can\s+(?:you|jane|casedb)|what\s+are\s+your\s+(?:tools|capabilities|functions)|(?:list|show|explain)\s+(?:your\s+)?(?:tools|capabilities|functions)|help\s+menu|capability\s+list|tool\s+list|functionality\s+list)\b/.test(normalized)) {
        return 'Loading the current Jane capability registry…';
    }
    if (/\b(settlement|demand|dtf|trial figure|forecast|value|valuation|evaluate|review)\b/.test(normalized)) {
        if (hasSpreadsheetReference || hasUploadReference) return 'Reviewing the uploaded data against CaseDB values…';
        return 'Checking settlement, demand, and value signals…';
    }
    if (/\b(summarize|summary|what happened|timeline|overview)\b/.test(normalized)) {
        if (hasCaseReference) return 'Building a case-specific summary from source records…';
        return 'Pulling together the most relevant source excerpts…';
    }
    if (/\b(compare|difference|versus|vs\b|between)\b/.test(normalized)) {
        return 'Comparing the referenced CaseDB materials…';
    }
    if (hasSpreadsheetReference) return 'Reading spreadsheet evidence and related case context…';
    if (hasUploadReference) return 'Reviewing the uploaded file with CaseDB context…';
    if (hasCaseReference) return 'Searching this case’s documents and timeline…';

    return 'Searching CaseDB for the best supporting sources…';
};
